import { Dialog, DialogContent, DialogContentText, DialogActions, Button, DialogTitle, TextField } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import propTypes from "prop-types";
import { Product, ProductSaleItem } from "../../../../logic/api";
import ErrorDialog from "../../../../components/notifications/errorDialog/ErrorDialog";
import { Trans, useTranslation } from "react-i18next";

const ProductQtyDialog: React.FunctionComponent<{
    product: Product;
    initialQty: number;
    show: boolean;
    saleItems: ProductSaleItem[];
    onClose?: (result: number | null) => void;
}> = (props) => {
    const formatValue = (value: number | string | null | undefined) => {
        return parseInt((value == null ? "0" : value.toString())).toString();
    }

    const [show, setShow] = useState<boolean>(false);
    const [value, setValue] = useState<string>(formatValue(props.initialQty));
    const [err, setErr] = useState<Error | null>(null);

    const { t } = useTranslation();

    const prodMaxQty = props.product.maxQty ?? 0;
    const existingQty = props.saleItems
        .filter((saleItem) => !saleItem.voided)
        .filter((saleItem) => saleItem.originalProductId === props.product.id)
        .map((saleItem) => saleItem.qty)
        .reduce((acc, value) => acc += value, 0);
    const maxQty = Math.max(prodMaxQty - existingQty, 0);

    useEffect(() => {
        setShow(props.show);
        if (props.show == false) {
            setValue(formatValue(props.initialQty));
        }
    }, [props.show, props.initialQty]);

    const ref = useRef<HTMLInputElement>();

    return <Dialog
        open={show}
        keepMounted
        onClose={() => setShow(false)}
        aria-labelledby="alert-dialog-slide-title"
        aria-describedby="alert-dialog-slide-description"
    >
        <DialogTitle>
            <Trans i18nKey="dialog.productQuantity">
                {{ productName: props.product.name }} quantity:
            </Trans>
        </DialogTitle>
        <DialogContent>

            {prodMaxQty > 0 && (
                <DialogContentText id="alert-dialog-slide-description">
                    <Trans i18nKey="dialog.maximumQty">
                        Maximum: {{ quantity: maxQty }}
                    </Trans>
                </DialogContentText>
            )}



            <ErrorDialog err={err}></ErrorDialog>
            <br />


            <TextField
                inputRef={ref}
                type="number"
                size="small"

                label={t("input.labels.quantity")}
                variant="outlined"

                value={value}

                InputLabelProps={{
                    shrink: true,
                }}
                onChange={(e) => {
                    setValue(e.target.value);
                }}
                onBlur={(e) => {
                    const val = parseFloat(e.target.value);
                    if (isNaN(val)) {
                        if (ref.current) {
                            ref.current.value = props.initialQty.toString();
                        }
                        return;
                    }
                    setValue(e.target.value);

                }}
            />
        </DialogContent>
        <DialogActions>
            <Button onClick={() => {
                setShow(false);
                if (props.onClose) {
                    props.onClose(null);
                }
            }} color="primary">{t("general.cancel")}</Button>
            <Button onClick={() => {
                const intValue = parseInt(value);

                if (isNaN(intValue)) return;
                if (intValue < 1) return;

                if (intValue > maxQty && prodMaxQty > 0) {
                    setErr(
                        new Error(
                            t("errors.productMaxQty", { existingQuantity: existingQty + intValue, productMaxQuantity: prodMaxQty })
                        )
                    );
                    return;
                }
                setShow(false);
                if (props.onClose) {
                    props.onClose(intValue);
                }
            }} color="primary"><b>{t("general.ok")}</b></Button>
        </DialogActions>
    </Dialog>
}

ProductQtyDialog.propTypes = {
    product: propTypes.any.isRequired,
    initialQty: propTypes.number.isRequired,
    show: propTypes.bool.isRequired,
    onClose: propTypes.func.isRequired,
    saleItems: propTypes.any.isRequired
}

export default ProductQtyDialog;
